import ClearIcon from '@mui/icons-material/Clear';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import { Box, Card, CardContent, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import {
  DEFAULT_CATEGORY_ID,
  type BlueprintSection,
  type DefinitionColumnConfig,
  type DefinitionDataSection,
  type Language,
  type LocalizedLabel,
} from '../../../types';
import { useDefinitionContext } from '../../contexts/DefinitionContext';
import { ColumnTypeConfigEditor } from '../column-types/ColumnTypeConfigEditor';
import { LocalizedLabelEditor } from '../LocalizedLabelEditor';
import { ColumnTypeChip } from './ColumnTypeChip';

type ColumnType = NonNullable<NonNullable<DefinitionDataSection['columns']>>[number];

interface ColumnCardProps {
  column: ColumnType;
  blueprintColumn?: BlueprintSection['columns'][number];
  index: number;
  totalColumns: number;
  onRemove: () => void;
  onLabelChange: (label: LocalizedLabel | undefined) => void;
  onTypeChange?: (config: DefinitionColumnConfig) => void;
  onMoveToTop?: () => void;
  onMoveToBottom?: () => void;
  /** Props spread onto the drag handle (e.g. from @dnd-kit useSortable). */
  dragHandleProps?: Record<string, unknown>;
}

function resolveLabel(
  label: LocalizedLabel | undefined,
  language: Language,
  fallback: string,
): string {
  if (!label) return fallback;
  return label[language] || fallback;
}

export function ColumnCard({
  column,
  blueprintColumn,
  index,
  totalColumns,
  onRemove,
  onLabelChange,
  onTypeChange,
  onMoveToTop,
  onMoveToBottom,
  dragHandleProps,
}: ColumnCardProps) {
  const { $t } = useIntl();
  const { languages } = useDefinitionContext();
  const primaryLanguage = languages[0] || 'en';
  // Extract category from id (format: category:key)
  const categoryFromId = column.id?.split(':')[0] || DEFAULT_CATEGORY_ID;
  const categoryLabel =
    categoryFromId !== DEFAULT_CATEGORY_ID ? categoryFromId : DEFAULT_CATEGORY_ID;

  // Use blueprint defaultLabel for the title (fixed), fallback to column.label if not available
  const defaultLabel = blueprintColumn?.defaultLabel as LocalizedLabel | undefined;
  const columnLabel = resolveLabel(
    defaultLabel,
    primaryLanguage,
    column.id?.split(':')[1] || column.id || '',
  );

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="flex-start" gap={1}>
          <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
            <Chip label={index + 1} size="small" sx={{ minWidth: 32 }} />
            <Box
              className="drag-handle"
              {...dragHandleProps}
              sx={{
                cursor: 'grab',
                display: 'flex',
                alignItems: 'center',
                padding: 0.5,
                touchAction: 'none',
                '&:active': {
                  cursor: 'grabbing',
                },
              }}
            >
              <DragIndicatorIcon />
            </Box>
          </Box>
          <Box flex={1} ml={1}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} gap={2}>
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="body1" fontWeight="500">
                  {columnLabel}
                </Typography>
                <ColumnTypeChip type={column.config.type} />
              </Box>
              {categoryLabel !== DEFAULT_CATEGORY_ID && (
                <Chip label={categoryLabel} size="small" variant="outlined" />
              )}
            </Box>
            <Box marginTop={1}>
              <LocalizedLabelEditor
                value={column.label}
                onChange={onLabelChange}
                languages={languages}
                label={$t({
                  id: 'audit.reportDefinitionEditor.columnLabel',
                  defaultMessage: 'Título',
                })}
              />
            </Box>
            {onTypeChange && <ColumnTypeConfigEditor column={column} onTypeChange={onTypeChange} />}
          </Box>
          <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
            <IconButton size="small" onClick={onRemove} color="error">
              <ClearIcon />
            </IconButton>
            {onMoveToTop && index > 0 && (
              <Tooltip
                title={$t({
                  id: 'audit.column.moveToStart',
                  defaultMessage: 'Mover al inicio',
                })}
                placement="right"
              >
                <IconButton size="small" onClick={onMoveToTop}>
                  <VerticalAlignTopIcon />
                </IconButton>
              </Tooltip>
            )}
            {onMoveToBottom && index < totalColumns - 1 && (
              <Tooltip
                title={$t({
                  id: 'audit.column.moveToEnd',
                  defaultMessage: 'Mover al final',
                })}
                placement="right"
              >
                <IconButton size="small" onClick={onMoveToBottom}>
                  <VerticalAlignBottomIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
